/**
 * Safe JSON parsing utilities.
 *
 * URL params are user-controlled and can be corrupted (stale bookmarks, shared
 * links with invalid payloads, format migrations). Raw JSON.parse throws on
 * malformed input, crashing the render tree and leaving the bad URL persisted
 * in routing storage — see ENG-128897.
 */

export interface SafeJsonParseOptions {
  /**
   * Whether to emit a console.warn when fallback is triggered.
   * Defaults to true.
   */
  warn?: boolean;
  /** Label included in the warning to identify the callsite. */
  context?: string;
}

/**
 * Parse `raw` as JSON, returning `fallback` on any failure.
 *
 * A console.warn is emitted whenever the fallback is triggered (opt out via
 * `options.warn = false`), so that malformed URL params are visible in all
 * environments — not just development.
 */
export function safeJsonParse<T>(
  raw: string | null | undefined,
  fallback: T,
  options: SafeJsonParseOptions = {},
): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    if (options.warn !== false) {
      const ctx = options.context ?? 'safeJsonParse';
      const snippet = raw.length > 200 ? raw.slice(0, 200) + '…' : raw;

      console.warn(`[${ctx}] Falling back due to invalid JSON: ${snippet}`);
    }
    return fallback;
  }
}
